import { IonCol, IonGrid, IonRow } from '@ionic/react';
import React, { Suspense } from 'react';
import { safeLazy } from 'react-safe-lazy';
import { useSettings } from '../../contexts/SettingsContext';
import { DatasourceDetailsSection } from '../../types';
import { DatasourceHeader } from './datasource-header';
import { HeaderDivider } from './header-divider';

// Lazy load all sections
const CurrentAQSection = safeLazy(() => import('../../sections/CurrentAQSection'));
const HealthRecommendationSection = safeLazy(() => import('../../sections/HealthRecommendationSection'));
const LocationSection = safeLazy(() => import('../../sections/LocationSection'));
const MeasurementsSection = safeLazy(() => import('../../sections/MeasurementsSection'));
const MonitoringDeviceSection = safeLazy(() => import('../../sections/MonitoringDeviceSection'));
const DownloadMeasurementsSection = safeLazy(() => import('../../sections/DownloadSection'));
const DistanceFromLocationSection = safeLazy(() => import('../../sections/DistanceFromLocationSection'));

interface DatasourceDetailsContainerProps {
  onClose: () => void;
}

const ALL_SECTIONS: Record<DatasourceDetailsSection, () => React.ReactNode> = {
  [DatasourceDetailsSection.CURRENT]: () => <CurrentAQSection />,
  [DatasourceDetailsSection.HEALTH_RECOMMENDATIONS]: () => <HealthRecommendationSection />,
  [DatasourceDetailsSection.LOCATION]: () => <LocationSection />,
  [DatasourceDetailsSection.TREND]: () => <MeasurementsSection />,
  [DatasourceDetailsSection.EQUIPMENT]: () => <MonitoringDeviceSection />,
  [DatasourceDetailsSection.DOWNLOAD]: () => <DownloadMeasurementsSection />,
};

const DatasourceDetailsContainer: React.FC<DatasourceDetailsContainerProps> = ({ onClose }) => {
  const { marker, network, setSearchedLocation, setMyLocation } = useSettings();

  const enabledSections = React.useMemo(() => {
    if (!marker || !network) {
      return [];
    }

    const { suppressOpenLocation } = marker;
    return (network.datasourceDetailsSections ?? []).filter(
      (sectionName) =>
        sectionName in ALL_SECTIONS && (sectionName != DatasourceDetailsSection.LOCATION || !suppressOpenLocation)
    );
  }, [network, marker]);

  const onClearSearch = React.useCallback(() => {
    setSearchedLocation(null);
    setMyLocation(null);
  }, [setSearchedLocation, setMyLocation]);

  return (
    <IonGrid>
      <IonRow>
        <IonCol>
          <DatasourceHeader onClose={onClose} marker={marker} network={network} />
        </IonCol>
      </IonRow>
      <HeaderDivider />
      <Suspense fallback={<div>Loading...</div>}>
        <IonRow>
          <DistanceFromLocationSection onClose={onClearSearch} />
        </IonRow>
        {enabledSections.map((sectionName) => (
          <IonRow key={sectionName}>
            <IonCol>
              <Suspense fallback={null}>{ALL_SECTIONS[sectionName]()}</Suspense>
            </IonCol>
          </IonRow>
        ))}
      </Suspense>
    </IonGrid>
  );
};

export default DatasourceDetailsContainer;
